<template>
  <div class="crx-root">
    <div class="crx-daterange">
      <div class="crx-daterange__presets">
        <button
          v-for="preset in DATE_PRESETS" :key="preset.value"
          type="button" class="crx-daterange__chip"
          :class="{ 'is-active': datePreset === preset.value }"
          @click="setDatePreset(preset.value)"
        >{{ preset.title }}</button>
      </div>
      <div v-if="datePreset === 'custom'" class="crx-daterange__custom">
        <input v-model="customDateFrom" type="date" class="crx-daterange__input" @change="applyCustomRange" />
        <span class="crx-daterange__sep">–</span>
        <input v-model="customDateTo" type="date" class="crx-daterange__input" @change="applyCustomRange" />
      </div>
      <span class="crx-daterange__label">{{ dateRangeLabel }}</span>
    </div>

    <p class="crx-note">
      These are full row-level data dumps (not the Dashboard's aggregated numbers) built in the
      background so they can handle very large campaigns -- expect a short wait after clicking.
    </p>

    <div class="crx-grid">
      <article v-for="report in reportDefs" :key="report.key" class="crx-card">
        <div class="crx-card__icon"><v-icon :icon="report.icon" size="22" /></div>
        <div class="crx-card__body">
          <h3 class="crx-card__title">{{ report.title }}</h3>
          <p class="crx-card__desc">{{ report.desc }}</p>
        </div>
        <p v-if="jobs[report.key]?.status === 'FAILED'" class="crx-card__error">
          {{ jobs[report.key].message || 'Export failed.' }}
        </p>
        <p v-else-if="jobs[report.key]?.status === 'RUNNING' || jobs[report.key]?.status === 'PENDING'" class="crx-card__progress">
          Preparing{{ jobs[report.key].rowCount ? ` — ${jobs[report.key].rowCount} rows so far` : '' }}…
        </p>
        <button
          type="button" class="crx-card__button"
          :disabled="isBusy(report.key) || !campaignId"
          @click="download(report.key)"
        >
          <v-icon v-if="isBusy(report.key)" icon="mdi-loading" class="crx-spin" size="16" />
          <v-icon v-else icon="mdi-file-download-outline" size="16" />
          {{ isBusy(report.key) ? 'Preparing…' : 'Download CSV' }}
        </button>
      </article>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import {
  fetchReportExportStatus,
  startCampaignReportExport,
  startScopeReportExport,
} from '@/api/campaignsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'

const props = defineProps({
  campaignId: { type: String, default: '' },
  campaignName: { type: String, default: '' },
  convertedStatus: { type: String, default: 'CONVERTED' },
  // Same convention as CampaignDashboardPanel.vue's own scopeType prop --
  // 'CAMPAIGN' (default) keeps this exactly as it always was; 'PRODUCT'/
  // 'ORGANIZATION' route through startScopeReportExport instead, with
  // campaignId reused as "the scope id".
  scopeType: { type: String, default: 'CAMPAIGN' },
})

// These are deliberately raw, row-level data dumps -- not aggregated
// numbers, which already live on the Dashboard tab and don't need a
// separate export. Each is the underlying activity the dashboard's
// numbers are computed FROM. Built server-side and delivered as CSV: a
// campaign can have hundreds of thousands of rows, and CSV can be streamed
// out of the Lambda without the memory/complexity a "real" .xlsx writer
// would need at that scale -- Excel opens a .csv natively either way.
const reportDefs = [
  {
    key: 'callLog',
    title: 'Call log',
    desc: 'Every individual call: lead, agent, time, duration, outcome -- one row per call.',
    icon: 'mdi-phone-log-outline',
  },
  {
    key: 'statusHistory',
    title: 'Status change history',
    desc: 'Every status transition: previous status, new status, who changed it, when -- one row per event.',
    icon: 'mdi-timeline-clock-outline',
  },
  {
    key: 'agentActivity',
    title: 'Agent activity log',
    desc: 'Per agent, per day: calls placed/connected, leads touched, status changes, active window.',
    icon: 'mdi-account-clock-outline',
  },
  {
    key: 'leadDetail',
    title: 'Full lead detail dump',
    desc: 'Every lead: status, score, assigned agent, custom fields, plus call/status activity in range.',
    icon: 'mdi-table-large',
  },
]

// Per-report-card job state: { status: 'PENDING'|'RUNNING'|'DONE'|'FAILED', message, rowCount }
const jobs = reactive({})
const pollTimers = {}

function isBusy(key) {
  return jobs[key]?.status === 'PENDING' || jobs[key]?.status === 'RUNNING'
}

const scopeNoun = computed(() => {
  if (props.scopeType === 'PRODUCT') return 'Product'
  if (props.scopeType === 'ORGANIZATION') return 'Organization'
  return 'Campaign'
})
const DATE_PRESETS = computed(() => [
  { value: '7d', title: '7d' },
  { value: '30d', title: '30d' },
  { value: 'campaign', title: scopeNoun.value },
  { value: 'custom', title: 'Custom' },
])
const datePreset = ref('campaign')
const customDateFrom = ref('')
const customDateTo = ref('')
function toDateStr(d) { return d.toISOString().slice(0, 10) }
const dateRange = computed(() => {
  const today = new Date()
  if (datePreset.value === '7d') {
    const from = new Date(today); from.setDate(from.getDate() - 6)
    return { date_from: toDateStr(from), date_to: toDateStr(today) }
  }
  if (datePreset.value === '30d') {
    const from = new Date(today); from.setDate(from.getDate() - 29)
    return { date_from: toDateStr(from), date_to: toDateStr(today) }
  }
  if (datePreset.value === 'custom') {
    return { date_from: customDateFrom.value || undefined, date_to: customDateTo.value || undefined }
  }
  return { date_from: undefined, date_to: undefined }
})
const dateRangeLabel = computed(() => {
  const { date_from: from, date_to: to } = dateRange.value
  if (!from && !to) return `Full ${scopeNoun.value.toLowerCase()} (live)`
  if (from && to) return `${from} – ${to}`
  return from || to || ''
})
function setDatePreset(value) { datePreset.value = value }
function applyCustomRange() {}

async function download(key) {
  if (!props.campaignId || isBusy(key)) return
  jobs[key] = { status: 'PENDING', message: '', rowCount: 0 }

  const response = props.scopeType === 'CAMPAIGN'
    ? await startCampaignReportExport({
        task_id: props.campaignId, report_type: key, ...dateRange.value,
      })
    : await startScopeReportExport({
        scope_type: props.scopeType, scope_id: props.campaignId, report_type: key, ...dateRange.value,
      })
  if (!isApiSuccess(response)) {
    jobs[key] = { status: 'FAILED', message: getApiMessage(response, 'Failed to start export') }
    return
  }
  const exportId = (response?.data ?? response)?.export_id
  if (!exportId) {
    jobs[key] = { status: 'FAILED', message: 'No export id returned.' }
    return
  }
  jobs[key].status = 'RUNNING'
  pollExport(key, exportId)
}

function pollExport(key, exportId) {
  const poll = async () => {
    const response = await fetchReportExportStatus(exportId)
    if (!isApiSuccess(response)) {
      jobs[key] = { status: 'FAILED', message: getApiMessage(response, 'Export failed') }
      return
    }
    const data = response?.data ?? response
    const status = String(data?.status || '').toUpperCase()
    if (status === 'DONE') {
      jobs[key] = { status: 'DONE', rowCount: data.row_count || 0 }
      triggerDownload(data.download_url, key)
      // Clear the card back to idle a moment after the browser download fires.
      setTimeout(() => { if (jobs[key]?.status === 'DONE') delete jobs[key] }, 4000)
      return
    }
    if (status === 'FAILED') {
      jobs[key] = { status: 'FAILED', message: data.error_message || 'Export failed.' }
      return
    }
    jobs[key] = { status: status || 'RUNNING', rowCount: data?.row_count || 0 }
    pollTimers[key] = setTimeout(poll, 2500)
  }
  poll()
}

function triggerDownload(url, key) {
  if (!url) return
  const a = document.createElement('a')
  a.href = url
  a.download = `${props.campaignName || 'campaign'}_${key}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
}

onBeforeUnmount(() => {
  for (const timer of Object.values(pollTimers)) clearTimeout(timer)
})
</script>

<style scoped>
.crx-root { display: flex; flex-direction: column; gap: 1.25rem; }

.crx-daterange { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.crx-daterange__presets { display: inline-flex; gap: 2px; background: var(--tw-surface-2); border: 1px solid var(--tw-line); border-radius: 7px; padding: 2px; }
.crx-daterange__chip {
  appearance: none; border: none; background: none;
  font: inherit; font-size: 12px; font-weight: 600;
  color: var(--tw-muted); cursor: pointer;
  padding: 5px 11px; border-radius: 5px;
}
.crx-daterange__chip.is-active { background: var(--tw-card); color: var(--tw-text); box-shadow: var(--tw-shadow); }
.crx-daterange__custom { display: inline-flex; align-items: center; gap: 6px; }
.crx-daterange__input {
  font: inherit; font-size: 12px; color: var(--tw-text);
  background: var(--tw-surface-2); border: 1px solid var(--tw-line);
  border-radius: 6px; padding: 5px 8px;
}
.crx-daterange__sep { color: var(--tw-muted); font-size: 12px; }
.crx-daterange__label { font-size: 12px; color: var(--tw-muted); }

.crx-note { margin: 0; font-size: 12.5px; line-height: 1.5; color: var(--tw-muted); max-width: 44rem; }

.crx-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.crx-card {
  display: flex; flex-direction: column; gap: 0.75rem;
  background: var(--tw-card); border: 1px solid var(--tw-line);
  border-radius: var(--tw-radius); padding: 1.1rem 1.15rem;
}
.crx-card__icon {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: var(--tw-accent-soft); color: var(--tw-accent); flex: none;
}
.crx-card__body { flex: 1 1 auto; }
.crx-card__title { margin: 0 0 0.3rem; font-size: 0.95rem; font-weight: 700; color: var(--tw-text); }
.crx-card__desc { margin: 0; font-size: 0.8rem; line-height: 1.5; color: var(--tw-muted); }
.crx-card__error { margin: 0; font-size: 0.78rem; color: var(--tw-danger); }
.crx-card__progress { margin: 0; font-size: 0.78rem; color: var(--tw-muted); }

.crx-card__button {
  align-self: flex-start;
  display: inline-flex; align-items: center; gap: 6px;
  font: inherit; font-size: 0.8rem; font-weight: 650;
  color: #fff; background: var(--tw-accent);
  border: none; border-radius: 8px; padding: 8px 14px;
  cursor: pointer; transition: filter 0.12s ease;
}
.crx-card__button:hover:not(:disabled) { filter: brightness(1.08); }
.crx-card__button:disabled { opacity: 0.65; cursor: default; }

.crx-spin { animation: crx-spin 0.9s linear infinite; }
@keyframes crx-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
